import React, { useEffect, useState } from 'react'
import { APP_SETTINGS } from '../../../constants/settings'

interface TouchControlsProps {
  onPrevious: () => void
  onNext: () => void
  onToggleIngredients: () => void
  onUpdateServings: (servings: number) => void
  currentServings: number
  showingIngredients: boolean
  canGoNext: boolean
  canGoPrevious: boolean
}

const TouchControls: React.FC<TouchControlsProps> = ({

  onPrevious,
  onNext,
  onToggleIngredients,
  onUpdateServings,
  currentServings,
  showingIngredients,
  canGoNext,
  canGoPrevious
}) => {
  console.log('⚡ TouchControls: Component rendering')
  
  // Add state to track which button is being pressed
  const [activeButton, setActiveButton] = useState<string | null>(null);

  // Helper function to handle touch start
  const handleTouchStart = (buttonId: string, action: () => void, e: React.TouchEvent) => {
    console.log(`${buttonId} touchstart`);
    setActiveButton(buttonId);
    action();
    e.stopPropagation();
  };

  // Helper function to handle touch end
  const handleTouchEnd = (e: React.TouchEvent) => {
    console.log(`Touch end, clearing active button`);
    setActiveButton(null);
    e.stopPropagation();
  };
  // Add debug logs for all touch events
  useEffect(() => {
    // Log all touch events to debug issues
    const touchEventHandler = (e: TouchEvent) => {
      console.log(`DEBUG - TouchControls touch event: ${e.type}`, {
        target: (e.target as any)?.tagName,
        targetId: (e.target as any)?.id || 'unknown-id',
        x: e.touches.length ? e.touches[0].clientX : 'no-touch',
        y: e.touches.length ? e.touches[0].clientY : 'no-touch'
      });
    };

    // Add listeners to the document
    document.addEventListener('touchstart', touchEventHandler, { capture: true });
    document.addEventListener('touchend', touchEventHandler, { capture: true });
    
    return () => {
      document.removeEventListener('touchstart', touchEventHandler, { capture: true });
      document.removeEventListener('touchend', touchEventHandler, { capture: true });
    };
  }, []);
  
  return (
    <div 
      id="touch-controls"
      data-touchcomponent="true"
      className="fixed bottom-0 left-0 right-0 bg-gray-900 bg-opacity-90 border-t border-gray-700 p-4 flex flex-col z-50"
      onTouchStart={() => {
        console.log('TouchControls container touchstart');
        // Don't stop propagation
      }}
      onClick={() => {
        console.log('TouchControls container click');
        // Don't stop propagation
      }}
    >
      {/* Servings Control */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-white text-lg">Servings</span>
        <div className="flex items-center space-x-4">
          <button
            id="minus-servings-button"
            data-touchbutton="minus-servings"
            role="button"
            aria-label="Decrease servings"
            onClick={(e) => {
              console.log('Minus servings button clicked');
              onUpdateServings(Math.max(1, currentServings - 1));
              e.stopPropagation();
            }}
            onTouchStart={(e) => handleTouchStart('minus-servings-button', () => onUpdateServings(Math.max(1, currentServings - 1)), e)}
            onTouchEnd={handleTouchEnd}
            className={`w-10 h-10 rounded-full ${activeButton === 'minus-servings-button' ? 'bg-green-500' : 'bg-gray-700'} text-white flex items-center justify-center`}
            disabled={currentServings <= 1}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <span className="text-white text-xl font-semibold w-6 text-center">{currentServings}</span>
          <button
            id="plus-servings-button"
            data-touchbutton="plus-servings"
            role="button"
            aria-label="Increase servings"
            onClick={(e) => {
              console.log('Plus servings button clicked');
              onUpdateServings(currentServings + 1);
              e.stopPropagation();
            }}
            onTouchStart={(e) => handleTouchStart('plus-servings-button', () => onUpdateServings(currentServings + 1), e)}
            onTouchEnd={handleTouchEnd}
            className={`w-10 h-10 rounded-full ${activeButton === 'plus-servings-button' ? 'bg-green-500' : 'bg-gray-700'} text-white flex items-center justify-center`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Main Controls */}
      <div className="flex justify-between">
        {/* Previous Button */}
        <button
          id="previous-button"
          data-touchbutton="previous"
          role="button"
          aria-label="Previous step"
          onClick={(e) => {
            console.log('Previous button clicked');
            onPrevious();
            e.stopPropagation();
          }}
          onTouchStart={(e) => handleTouchStart('previous-button', canGoPrevious ? onPrevious : () => {}, e)}
          onTouchEnd={handleTouchEnd}
          className={`${canGoPrevious ? (activeButton === 'previous-button' ? 'bg-green-500' : 'bg-green-800 hover:bg-green-700') : 'bg-gray-700 text-gray-500'} rounded-lg px-6 py-3 flex items-center justify-center w-1/4`}
          style={{ minHeight: `${APP_SETTINGS.TOUCH_TARGET_SIZE}px` }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        {/* Toggle Ingredients Button */}
        <button
          id="toggle-ingredients-button"
          data-touchbutton="toggle-ingredients"
          role="button"
          aria-label="Toggle ingredients view"
          onClick={(e) => {
            console.log('Toggle ingredients button clicked');
            onToggleIngredients();
            e.stopPropagation();
          }}
          onTouchStart={(e) => handleTouchStart('toggle-ingredients-button', onToggleIngredients, e)}
          onTouchEnd={handleTouchEnd}
          className={`${activeButton === 'toggle-ingredients-button' ? 'bg-green-500' : (showingIngredients ? 'bg-green-700' : 'bg-gray-700')} hover:bg-green-600 rounded-lg px-6 py-3 flex items-center justify-center space-x-2 text-white w-2/4`}
          style={{ minHeight: `${APP_SETTINGS.TOUCH_TARGET_SIZE}px` }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span>{showingIngredients ? 'Show Steps' : 'Show Ingredients'}</span>
        </button>
        
        {/* Next Button */}
        <button
          id="next-button"
          data-touchbutton="next"
          role="button"
          aria-label="Next step"
          onClick={(e) => {
            console.log('Next button clicked');
            onNext();
            e.stopPropagation();
          }}
          onTouchStart={(e) => handleTouchStart('next-button', canGoNext ? onNext : () => {}, e)}
          onTouchEnd={handleTouchEnd}
          className={`${canGoNext ? (activeButton === 'next-button' ? 'bg-green-500' : 'bg-green-800 hover:bg-green-700') : 'bg-gray-700 text-gray-500'} rounded-lg px-6 py-3 flex items-center justify-center w-1/4`}
          style={{ minHeight: `${APP_SETTINGS.TOUCH_TARGET_SIZE}px` }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default TouchControls
